/*
 * <CodeWell> -- a BeOS recessed well containing syntax-highlighted code.
 *
 * THE LOOK IS THE LIBRARY'S; THE ENGINE IS NOT. `.be-code-well` moved to
 * src/components/code-well.css under #278 B1, because a recessed well on the R5
 * palette is a design-system concern. This wrapper stays in demo/ because
 * src/react/index.ts is bundled whole (scripts/build.ts) -- promoting it would
 * put react-syntax-highlighter in dist/react for every consumer, and the library
 * is CSS-first: a page must never be forced to pull a highlighting engine. Same
 * split Primer makes, shipping .markdown-body without a renderer (Notes/73 7.3).
 *
 * THE ENGINE IS AN IMPLEMENTATION DETAIL OF THIS FILE. Callers pass a string and
 * a language; nothing else imports react-syntax-highlighter. That is the seam
 * for the eventual swap:
 *
 *   TODO: move to Shiki once this site renders server-side. Shiki highlights at
 *   BUILD time (no engine in the client bundle at all) but needs an async init,
 *   which is why it loses today -- these two call sites both highlight strings
 *   that change per keystroke / per fetch in the browser.
 *
 * `light` + explicit registerLanguage, not the default export: the default drags
 * in every grammar for both backends (the package depends on highlight.js AND
 * prismjs AND refractor AND lowlight).
 *
 * Colours come from CSS classes, never inline styles -- see code-well.css.
 */

import bash from "react-syntax-highlighter/dist/esm/languages/hljs/bash";
import c from "react-syntax-highlighter/dist/esm/languages/hljs/c";
import cpp from "react-syntax-highlighter/dist/esm/languages/hljs/cpp";
import cssLang from "react-syntax-highlighter/dist/esm/languages/hljs/css";
import json from "react-syntax-highlighter/dist/esm/languages/hljs/json";
import python from "react-syntax-highlighter/dist/esm/languages/hljs/python";
import scss from "react-syntax-highlighter/dist/esm/languages/hljs/scss";
import typescript from "react-syntax-highlighter/dist/esm/languages/hljs/typescript";
import xml from "react-syntax-highlighter/dist/esm/languages/hljs/xml";
import SyntaxHighlighter from "react-syntax-highlighter/dist/esm/light";

/** Registered once at module load; adding a language is one line here. */
const LANGUAGES = { bash, c, cpp, css: cssLang, json, python, scss, typescript, xml } as const;
for (const [name, grammar] of Object.entries(LANGUAGES)) {
	SyntaxHighlighter.registerLanguage(name, grammar);
}

/** Aliases a markdown fence may use for a grammar we registered. */
const ALIAS: Record<string, keyof typeof LANGUAGES> = {
	"c++": "cpp",
	cc: "cpp",
	h: "cpp",
	html: "xml",
	js: "typescript",
	jsx: "typescript",
	py: "python",
	sass: "scss",
	sh: "bash",
	shell: "bash",
	svg: "xml",
	ts: "typescript",
	tsx: "typescript",
};

export type CodeWellProps = {
	children: string;
	/** A fence language ("css", "ts", "sh", ...). Unknown ones render unhighlighted. */
	language?: string;
	/** Wrap long lines instead of scrolling them. Off suits code, on suits output. */
	wrap?: boolean;
	/** Dracula's dark palette instead of the kit's light one. */
	dark?: boolean;
	className?: string;
};

export function CodeWell({ children, language, wrap = false, dark = false, className }: CodeWellProps) {
	const key = language?.toLowerCase() ?? "";
	const resolved = key in LANGUAGES ? key : ALIAS[key];
	const classes = cx("be-code-well", dark && "is-dark", className);

	// An unregistered language would throw inside lowlight, so fall back to a
	// plain well rather than taking the page down over a stray fence label.
	if (!resolved) {
		return <pre className={classes}>{children}</pre>;
	}

	return (
		<SyntaxHighlighter
			language={resolved}
			// keeps every colour in CSS, so the BeOS palette themes it
			useInlineStyles={false}
			wrapLongLines={wrap}
			className={classes}
		>
			{children}
		</SyntaxHighlighter>
	);
}

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(" ");
